import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext.jsx";
import { getEmpresas, createUser, updateUser, BACKEND_URL } from "../../api/api";
import { getGruposTrabajo } from "../../api/gruposTrabajo";
import { getJornadas } from "../../api/jornadas";
import { isValidEmail, isValidName, isValidPassword, isValidDNI, isValidPhone } from "../../utils/validators";
import { VALIDATION_MESSAGES } from "../../utils/constants";

/**
 * Componente de formulario para crear o editar usuarios.
 * Maneja la validación de datos, selección de empresas, grupos y jornadas,
 * carga de imagen de perfil y envío de datos al backend.
 * 
 * @param {Object} props
 * @param {Object} [props.user] - Usuario a editar (null para crear nuevo)
 * @param {Function} [props.onBack] - Callback al volver atrás
 * @param {Function} [props.onSaved] - Callback después de guardar exitosamente
 * @returns {JSX.Element} Formulario completo de usuario
 */
export default function CreateUserPage({ user = null, onBack = null, onSaved = null }) {
  const { token, user: me, reloadUser } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [jornadasList, setJornadasList] = useState([]);
  const [jornadasDropdownOpen, setJornadasDropdownOpen] = useState(false);
  const [selectedGrupo, setSelectedGrupo] = useState("");
  const [selectedJornadas, setSelectedJornadas] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const isEdit = !!user;

  const [form, setForm] = useState({
    nombre: user?.nombre || "",
    segundoNombre: user?.segundoNombre || "",
    primerApellido: user?.primerApellido || "",
    segundoApellido: user?.segundoApellido || "",
    dni: user?.dni || "",
    telefono: user?.telefono || "",
    email: user?.email || "",
    password: "",
    role: user?.role || "trabajador",
    empresa: user?.empresa?._id || "",
    imagenPerfil: null,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getEmpresas(token);
        setEmpresas(data);
        try { setGrupos(await getGruposTrabajo(token)); } catch (e) { }
        try { setJornadasList(await getJornadas(token)); } catch (e) { }
      } catch (err) {
      }
    };
    load();
  }, [token]);

  // Inicializar selección de jornadas y grupo cuando cargue el usuario / listas
  useEffect(() => {
    if (user) {
      // jornadas del usuario
      if (user.jornadas && Array.isArray(user.jornadas)) {
        setSelectedJornadas(user.jornadas.map(j => typeof j === 'object' ? j._id : j));
      }
    }
  }, [user]);

  useEffect(() => {
    if (user && grupos && grupos.length) {
      const g = grupos.find(gp => (gp.empleados || []).map(String).includes(String(user._id)));
      if (g) setSelectedGrupo(g._id);
    }
  }, [user, grupos]);

  useEffect(() => {
    // Set image preview from existing user image
    if (user?.imagenPerfil) {
      setImagePreview(`${BACKEND_URL}${user.imagenPerfil}`);
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");

    // Validaciones
    if (!isValidName(form.nombre.trim())) {
      setErrorMessage(VALIDATION_MESSAGES.INVALID_NAME);
      return;
    }
    if (form.segundoNombre && !isValidName(form.segundoNombre.trim())) {
      setErrorMessage(VALIDATION_MESSAGES.INVALID_SECOND_NAME);
      return;
    }
    if (!isValidName(form.primerApellido.trim())) {
      setErrorMessage(VALIDATION_MESSAGES.INVALID_FIRST_SURNAME);
      return;
    }
    if (form.segundoApellido && !isValidName(form.segundoApellido.trim())) {
      setErrorMessage(VALIDATION_MESSAGES.INVALID_SECOND_SURNAME);
      return;
    }
    if (!isValidDNI(form.dni.trim().toUpperCase())) {
      setErrorMessage(VALIDATION_MESSAGES.INVALID_DNI);
      return;
    }
    if (!isValidPhone(form.telefono.trim())) {
      setErrorMessage(VALIDATION_MESSAGES.INVALID_PHONE);
      return;
    }
    if (!isValidEmail(form.email.trim())) {
      setErrorMessage(VALIDATION_MESSAGES.INVALID_EMAIL);
      return;
    }
    
    // Password validation: required for create, optional for edit
    if (!isEdit && !isValidPassword(form.password)) {
      setErrorMessage(VALIDATION_MESSAGES.INVALID_PASSWORD);
      return;
    }
    if (isEdit && form.password && !isValidPassword(form.password)) {
      setErrorMessage(VALIDATION_MESSAGES.INVALID_PASSWORD_EDIT);
      return;
    }
    
    if (me.role === "global_admin" && form.role !== "global_admin" && !form.empresa) {
      setErrorMessage(VALIDATION_MESSAGES.EMPRESA_REQUIRED);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("nombre", form.nombre.trim());
      if (form.segundoNombre) formData.append("segundoNombre", form.segundoNombre.trim());
      formData.append("primerApellido", form.primerApellido.trim());
      if (form.segundoApellido) formData.append("segundoApellido", form.segundoApellido.trim());
      formData.append("dni", form.dni.trim().toUpperCase());
      formData.append("telefono", form.telefono.trim());
      formData.append("email", form.email.trim());
      if (form.password) formData.append("password", form.password);
      formData.append("role", form.role);
      if (form.role !== "global_admin" && form.empresa) {
        formData.append("empresa", form.empresa);
      }
      if (form.imagenPerfil) {
        formData.append("imagen", form.imagenPerfil);
      }
      // Grupo y jornadas opcionales
      if (selectedGrupo) formData.append("grupoTrabajo", selectedGrupo);
      if (selectedJornadas && Array.isArray(selectedJornadas)) {
        selectedJornadas.forEach(j => formData.append("jornadas", j));
      }

      if (isEdit) {
        await updateUser(token, user._id, formData);
        
        // Si el usuario editado es el mismo que está logueado, recargar sus datos
        if (me && user._id === me._id) {
          await reloadUser();
        }
        
        setSuccessMessage("Usuario actualizado correctamente");
        setTimeout(() => {
          if (onSaved) onSaved();
          else if (onBack) onBack();
        }, 1500);
      } else {
        await createUser(token, formData);
        setSuccessMessage("Usuario creado correctamente");
        
        // Resetear formulario
        setForm({
          nombre: "",
          segundoNombre: "",
          primerApellido: "",
          segundoApellido: "",
          dni: "",
          telefono: "",
          email: "",
          password: "",
          role: "trabajador",
          empresa: "",
          imagenPerfil: null,
        });
        setImagePreview(null);
        setSelectedGrupo("");
        setSelectedJornadas([]);
        
        setTimeout(() => setSuccessMessage(""), 5000);
      }
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message || `Error al ${isEdit ? 'actualizar' : 'crear'} usuario`
      );
    }
  };

  const handleRoleChange = (newRole) => {
    setForm((s) => ({
      ...s,
      role: newRole,
      empresa: newRole === "global_admin" ? "" : s.empresa,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm({ ...form, imagenPerfil: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setForm({ ...form, imagenPerfil: null });
    setImagePreview(null);
  };

  return (
    <div className="max-w-7xl mx-auto p-2 sm:p-0">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-5 md:p-6">
        <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
          {isEdit && onBack && (
            <button
              type="button"
              onClick={onBack}
              className="text-gray-600 hover:text-gray-800 transition"
              title="Volver"
            >
              <i className="fas fa-arrow-left text-xl md:text-2xl"></i>
            </button>
          )}
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
            <i className={`fas ${isEdit ? 'fa-user-edit' : 'fa-user-plus'} mr-2`}></i>
            {isEdit ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Grid de 2 columnas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            
            {/* COLUMNA IZQUIERDA - Información Personal + Avatar */}
            <div className="space-y-4 md:space-y-6">
              {/* Avatar Section */}
              <div className="bg-gray-50 rounded-xl p-4 md:p-6 border border-gray-200">
                <h2 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4 flex items-center gap-2">
                  <i className="fas fa-camera text-gray-600"></i>
                  Imagen de Perfil
                </h2>
                
                <div className="flex flex-col items-center">
                  {/* Preview del avatar */}
                  <div className="relative mb-3 md:mb-4">
                    <img
                      src={
                        imagePreview ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(
                          (form.nombre || "U") + " " + (form.primerApellido || "N")
                        )}&background=4F46E5&color=fff&size=200`
                      }
                      alt="Preview"
                      className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                    {imagePreview && (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 bg-red-500 text-white w-7 h-7 sm:w-8 sm:h-8 rounded-full hover:bg-red-600 transition shadow-md flex items-center justify-center text-xs sm:text-sm"
                        title="Quitar imagen"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>

                  {/* Input de archivo */}
                  <label className="w-full cursor-pointer">
                    <div className="flex items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-300 hover:border-gray-500 rounded-lg p-3 md:p-4 transition">
                      <i className="fas fa-upload text-gray-600 text-sm md:text-base"></i>
                      <span className="text-xs sm:text-sm text-gray-700 font-medium">
                        {imagePreview ? "Cambiar imagen" : "Seleccionar imagen"}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Formatos: JPG, PNG, GIF (máx. 5MB)
                  </p>
                </div>
              </div>

              {/* Información Personal */}
              <div className="bg-gray-50 rounded-xl p-4 md:p-6 border border-gray-200">
                <h2 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4 flex items-center gap-2">
                  <i className="fas fa-user text-gray-600"></i>
                  Información Personal
                </h2>
                
                <div className="space-y-3 md:space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        Nombre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.nombre}
                        onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                        className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 text-sm md:text-base"
                        placeholder="Juan"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        Segundo Nombre
                      </label>
                      <input
                        type="text"
                        value={form.segundoNombre}
                        onChange={(e) => setForm({ ...form, segundoNombre: e.target.value })}
                        className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 text-sm md:text-base"
                        placeholder="Carlos"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        Primer Apellido <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.primerApellido}
                        onChange={(e) => setForm({ ...form, primerApellido: e.target.value })}
                        className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 text-sm md:text-base"
                        placeholder="Pérez"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        Segundo Apellido
                      </label>
                      <input
                        type="text"
                        value={form.segundoApellido}
                        onChange={(e) => setForm({ ...form, segundoApellido: e.target.value })}
                        className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 text-sm md:text-base"
                        placeholder="García"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        Documento de Identidad <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.dni}
                        onChange={(e) => setForm({ ...form, dni: e.target.value.toUpperCase() })}
                        className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 uppercase text-sm md:text-base"
                        placeholder="DNI/NIE/Pasaporte"
                        maxLength="20"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">DNI: 12345678A | NIE: X1234567A | Pasaporte: ABC123456</p>
                    </div>

                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        Teléfono <span className="text-gray-400 text-xs">(opcional)</span>
                      </label>
                      <input
                        type="tel"
                        value={form.telefono}
                        onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                        className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 text-sm md:text-base"
                        placeholder="600123456"
                        maxLength="9"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* COLUMNA DERECHA - Información de Cuenta */}
            <div className="space-y-4 md:space-y-6">
              <div className="bg-gray-50 rounded-xl p-4 md:p-6 border border-gray-200 h-full">
                <h2 className="text-base md:text-lg font-semibold text-gray-700 mb-3 md:mb-4 flex items-center gap-2">
                  <i className="fas fa-key text-gray-600"></i>
                  Información de Cuenta
                </h2>
                
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 text-sm md:text-base"
                      placeholder="usuario@ejemplo.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Contraseña {!isEdit && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 text-sm md:text-base"
                      placeholder={isEdit ? "Dejar vacío para no cambiar" : "Mínimo 6 caracteres"}
                      required={!isEdit}
                    />
                    {isEdit && (
                      <p className="text-xs text-gray-500 mt-1">
                        <i className="fas fa-info-circle mr-1"></i>
                        Dejar vacío si no deseas cambiar la contraseña
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      Rol <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.role}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 bg-white text-sm md:text-base"
                    >
                      <option value="trabajador">👤 Trabajador</option>
                      <option value="admin">🛡️ Admin empresa</option>
                      {me?.role === "global_admin" && (
                        <option value="global_admin">👑 Global Admin</option>
                      )}
                    </select>
                  </div>

                  {/* Empresa - Solo para global_admin puede seleccionar */}
                  {me?.role === "global_admin" && form.role !== "global_admin" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Empresa <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={form.empresa}
                        onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                        className="w-full border border-gray-300 p-2.5 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-500 bg-white"
                        required
                      >
                        <option value="">-- Selecciona una empresa --</option>
                        {empresas.map((emp) => (
                          <option key={emp._id} value={emp._id}>
                            {emp.nombre}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Empresa - Para admin muestra la suya (no editable) */}
                  {me?.role === "admin" && form.role !== "global_admin" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Empresa
                      </label>
                      <div className="w-full border border-gray-200 p-2.5 rounded-lg bg-gray-100 text-gray-600 flex items-center gap-2">
                        <i className="fas fa-lock text-gray-400"></i>
                        {me.empresa?.nombre || 'Tu empresa'}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Los usuarios que crees serán asignados automáticamente a tu empresa
                      </p>
                    </div>
                  )}

                  {/* Grupo de trabajo (opcional) - Solo para admin de empresa */}
                  {me?.role !== "global_admin" && (
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Grupo de trabajo (opcional)</label>
                      <select
                        className="w-full border border-gray-300 p-2 md:p-2.5 rounded-lg bg-white text-sm md:text-base"
                        value={selectedGrupo}
                        onChange={(e) => setSelectedGrupo(e.target.value)}
                      >
                        <option value="">-- Ninguno --</option>
                        {grupos.map(g => (
                          <option key={g._id} value={g._id}>{g.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Jornadas (múltiples) - Solo para admin de empresa */}
                  {me?.role !== "global_admin" && (
                    <div className="relative">
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Jornadas (opcional)</label>
                      <button
                        type="button"
                        onClick={() => setJornadasDropdownOpen(!jornadasDropdownOpen)}
                        className="border border-gray-300 p-2 md:p-2.5 rounded-lg bg-white hover:bg-gray-50 w-full flex items-center justify-between gap-2 text-xs md:text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <i className="fas fa-clock text-gray-400"></i>
                          <span className="truncate">{selectedJornadas.length === 0 ? "Seleccionar jornadas" : `${selectedJornadas.length} seleccionada(s)`}</span>
                        </div>
                        <i className={`fas fa-chevron-${jornadasDropdownOpen ? 'up' : 'down'} text-gray-400`}></i>
                      </button>

                      {jornadasDropdownOpen && (
                        <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl z-10 w-full max-h-56 overflow-y-auto">
                          <div className="p-2 border-b">
                            <button
                              type="button"
                              onClick={() => { setSelectedJornadas([]); setJornadasDropdownOpen(false); }}
                              className="text-sm text-gray-600 hover:text-gray-900 w-full text-left px-2 py-1"
                            >
                              Limpiar selección
                            </button>
                          </div>
                          {jornadasList.map((j) => (
                            <label key={j._id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedJornadas.includes(j._id)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedJornadas(prev => [...prev, j._id]);
                                  else setSelectedJornadas(prev => prev.filter(id => id !== j._id));
                                }}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">{j.nombre}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mensajes */}
          <div className="mt-4 md:mt-6 space-y-2 md:space-y-3">
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 md:px-4 md:py-3 rounded-lg flex items-center gap-2 text-xs md:text-sm">
                <i className="fas fa-exclamation-circle"></i>
                <span>{errorMessage}</span>
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 md:px-4 md:py-3 rounded-lg flex items-center gap-2 text-xs md:text-sm">
                <i className="fas fa-check-circle"></i>
                <span>{successMessage}</span>
              </div>
            )}
          </div>

          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-4 md:pt-6 mt-4 md:mt-6 border-t border-gray-200">
            {isEdit && onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 md:px-6 md:py-3 rounded-lg hover:bg-gray-200 transition font-semibold flex items-center justify-center gap-2 text-sm md:text-base"
              >
                <i className="fas fa-times"></i>
                Cancelar
              </button>
            )}
            <button
              type="submit"
              className="flex-1 bg-gray-800 text-white px-4 py-2.5 md:px-6 md:py-3 rounded-lg hover:bg-gray-900 transition font-semibold flex items-center justify-center gap-2 shadow-md text-sm md:text-base"
            >
              <i className="fas fa-save"></i>
              {isEdit ? 'Guardar Cambios' : 'Crear Usuario'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

